import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useParams } from 'react-router-dom'
import { FeatherLoader, useErrorService } from '@abroad/components'
import TruncatedText from '../OrgEco/TruncatedText'
import ExperienceButton from './ExperienceButton'
import { SearchIcon } from './ExperienceCopyModal'
import QuestGuidePairingModal, {
  getInitialPairingState,
} from './QuestGuidePairingModal'
import QuestAssignSOWModal, { getInitialSOWValues } from './QuestAssignSOWModal'
import {
  getGuideLabel,
  getParticipantAnswer,
  getParticipantEmail,
  getParticipantGuides,
  getParticipantId,
  getParticipantLatestAssessmentParts,
  getParticipantLatestAssessmentLink,
  getParticipantName,
  getParticipantSignupDate,
  getParticipantSOWs,
  getQuestionId,
  getQuestionTitle,
  toParticipantList,
} from './utils/guidePairingUtils'
import API from '../../utils/API'
import { formatDateTimeWithTimezone } from '../../utils/date'
import { getBrowserTimezone } from '../../utils/timezone'

const PAGE_LIMIT = 25
const SOW_NAME_MAX_LENGTH = 25
const SESSIONS_SHOWN = 3

const QuestGuidePairing = ({
  questDetail,
  enrollmentQuestions,
  onCompletenessChange,
}) => {
  const { questId: routeQuestId } = useParams()
  const questId = routeQuestId || questDetail?._id
  const ErrorService = useErrorService()
  const timezone = getBrowserTimezone()

  const hasMoreRef = useRef(false)
  const fetchInFlightRef = useRef(false)
  const loaderRef = useRef(null)
  const observerRef = useRef(null)
  const tableWrapRef = useRef(null)

  const [participants, setParticipants] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [query, setQuery] = useState({ page: 1, search: '' })
  const [answerQuestionId, setAnswerQuestionId] = useState('')
  // Keyed by participant id; keeps the row so selections survive a new search.
  const [selected, setSelected] = useState({})

  const [guides, setGuides] = useState([])
  const [isLoadingGuides, setIsLoadingGuides] = useState(false)
  const [showPairModal, setShowPairModal] = useState(false)
  const [showSowModal, setShowSowModal] = useState(false)
  const [pairingState, setPairingState] = useState(() =>
    getInitialPairingState(),
  )
  const [pendingPairs, setPendingPairs] = useState([])
  const [sowDraft, setSowDraft] = useState(null)
  // "participantId-guideIndex" keys whose full session list is expanded
  const [expandedSessions, setExpandedSessions] = useState(() => new Set())

  const toggleSessions = (key) =>
    setExpandedSessions((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })
  const [schedulingMeta, setSchedulingMeta] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      const search = searchInput.trim()
      setQuery((prev) =>
        prev.search === search && prev.page === 1 ? prev : { page: 1, search },
      )
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    if (!questId) return undefined
    let cancelled = false
    const append = query.page > 1
    fetchInFlightRef.current = true
    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
      setLoadError(false)
    }

    ;(async () => {
      try {
        const { data } = await API.AdminAPI.quests.getGuidePairingParticipants(
          questId,
          { page: query.page, limit: PAGE_LIMIT, search: query.search },
        )
        if (cancelled) return
        const rows = toParticipantList(data)
        const total = Number(data?.meta?.total)
        const more = Number.isFinite(total)
          ? query.page * PAGE_LIMIT < total
          : rows.length === PAGE_LIMIT
        hasMoreRef.current = more
        setHasMore(more)
        setParticipants((prev) => {
          if (!append) return rows
          const ids = new Set(prev.map(getParticipantId))
          return [...prev, ...rows.filter((p) => !ids.has(getParticipantId(p)))]
        })
        // meta counts are filtered by search, so only unsearched responses
        // represent the whole experience.
        if (!query.search && data?.meta) {
          const nextStats = {
            total: Number(data.meta.total) || 0,
            pairedCount: Number(data.meta.pairedCount) || 0,
          }
          if (typeof onCompletenessChange === 'function') {
            onCompletenessChange({ guidePairingStats: nextStats })
          }
        }
      } catch (e) {
        if (cancelled) return
        if (append) {
          ErrorService.showError(e)
        } else {
          setParticipants([])
          setLoadError(true)
        }
      } finally {
        fetchInFlightRef.current = false
        if (!cancelled) {
          setIsLoading(false)
          setIsLoadingMore(false)
        }
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, query])

  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore) {
      if (observerRef.current) observerRef.current.disconnect()
      return undefined
    }
    const el = loaderRef.current
    if (!el) return undefined
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreRef.current &&
          !fetchInFlightRef.current
        ) {
          setQuery((prev) => ({ ...prev, page: prev.page + 1 }))
        }
      },
      // The table scrolls inside its own wrapper, not the page.
      {
        root: tableWrapRef.current,
        rootMargin: '0px 0px 120px 0px',
        threshold: 0,
      },
    )
    observerRef.current.observe(el)
    return () => {
      if (observerRef.current) observerRef.current.disconnect()
    }
  }, [hasMore, isLoading, isLoadingMore, participants.length])

  const questions = useMemo(
    () => (enrollmentQuestions || []).filter((q) => !q?.isDeleted),
    [enrollmentQuestions],
  )
  const answerQuestion =
    questions.find((q) => getQuestionId(q) === answerQuestionId) || null

  const selectedParticipants = Object.values(selected)
  const selectedCount = selectedParticipants.length

  const visibleRows = participants.filter((p) => getParticipantId(p))
  const allVisibleChecked =
    visibleRows.length > 0 &&
    visibleRows.every((p) => selected[getParticipantId(p)])
  const someVisibleChecked =
    visibleRows.some((p) => selected[getParticipantId(p)]) && !allVisibleChecked

  const toggleOne = (participant) => {
    const id = getParticipantId(participant)
    setSelected((prev) => {
      const next = { ...prev }
      if (next[id]) {
        delete next[id]
      } else {
        next[id] = participant
      }
      return next
    })
  }

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = { ...prev }
      visibleRows.forEach((p) => {
        const id = getParticipantId(p)
        if (allVisibleChecked) {
          delete next[id]
        } else {
          next[id] = p
        }
      })
      return next
    })
  }

  const loadGuides = async () => {
    if (guides.length > 0 || isLoadingGuides) return
    setIsLoadingGuides(true)
    try {
      const { data } = await API.AdminAPI.teams.getCoachData()
      setGuides(
        (data || []).map((coach) => ({
          value: coach?._id,
          label: getGuideLabel(coach),
          fullName: coach?.fullName,
          isScheduleEnabled: coach?.isScheduleEnabled,
          hasVideoCallLink: coach?.hasVideoCallLink,
        })),
      )
    } catch (e) {
      ErrorService.showError(e)
    } finally {
      setIsLoadingGuides(false)
    }
  }

  const resetFlow = () => {
    setShowPairModal(false)
    setShowSowModal(false)
    setPendingPairs([])
    setSowDraft(null)
    setSchedulingMeta(null)
    setPairingState(getInitialPairingState())
  }

  const openPairing = () => {
    if (selectedCount === 0) return
    setPairingState(getInitialPairingState(selectedParticipants))
    setSowDraft(null)
    setShowPairModal(true)
    loadGuides()
  }

  const handlePairingNext = (pairs, meta) => {
    // Pairs can change guides on the way back, so a draft made for platform
    // scheduling must not survive a switch to forced manual scheduling.
    if (meta?.forceManualScheduling && !schedulingMeta?.forceManualScheduling) {
      setSowDraft(null)
    }
    setPendingPairs(pairs)
    setSchedulingMeta(meta)
    setShowPairModal(false)
    setShowSowModal(true)
  }

  const handleBackToPairing = (sowValues) => {
    setSowDraft(sowValues)
    setShowSowModal(false)
    setShowPairModal(true)
  }

  const handleAssigned = () => {
    resetFlow()
    setSelected({})
    setSearchInput('')
    setQuery({ page: 1, search: '' })
  }

  const renderAssessment = ({ type, date }) => (
    <>
      <div>{type}</div>
      {date ? <div>{date}</div> : null}
    </>
  )

  const renderSessions = (sessions = [], key) => {
    if (sessions.length === 0) return <span>No upcoming session</span>
    const isExpanded = expandedSessions.has(key)
    const visibleSessions = isExpanded
      ? sessions
      : sessions.slice(0, SESSIONS_SHOWN)
    return (
      <>
        <div className='font-bold'>Scheduled · {sessions.length}</div>
        {visibleSessions.map((session, i) => {
          const { date, time } = formatDateTimeWithTimezone(
            session.startTime,
            timezone,
          )
          return (
            <div key={`${session.startTime}-${i}`}>
              {date} at {time}
            </div>
          )
        })}
        {sessions.length > SESSIONS_SHOWN ? (
          <button
            type='button'
            className='btn btn-link p-0 text-dark-600 shadow-none'
            onClick={() => toggleSessions(key)}>
            {isExpanded
              ? 'Show less'
              : `+${sessions.length - SESSIONS_SHOWN} more`}
          </button>
        ) : null}
      </>
    )
  }

  const colCount = answerQuestion ? 7 : 6

  const renderEmptyRow = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={colCount} className='text-center py-3'>
            <FeatherLoader />
          </td>
        </tr>
      )
    }
    let message = 'No participants match that search.'
    if (loadError) {
      message = 'Participants could not be loaded right now.'
    } else if (!query.search) {
      message = 'Participants appear here after enrollment.'
    }
    return (
      <tr>
        <td colSpan={colCount}>
          <div className='experience-pay-empty' style={{ border: 0 }}>
            {message}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div className='experience-guide-pairing'>
      <div className='experience-gp-tools'>
        <select
          className='experience-filter'
          aria-label='Show enrollment answer'
          value={answerQuestionId}
          onChange={(e) => setAnswerQuestionId(e.target.value)}>
          <option value=''>No answer column</option>
          {questions.map((q) => (
            <option key={getQuestionId(q)} value={getQuestionId(q)}>
              {getQuestionTitle(q)}
            </option>
          ))}
        </select>
        <label className='experience-copy-search experience-gp-search'>
          <input
            type='search'
            placeholder='Search participants…'
            autoComplete='off'
            aria-label='Search participants'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <SearchIcon />
        </label>
        {/* Disabled buttons don't fire hover events, so the hint sits on a wrapper. */}
        <span
          title={
            selectedCount === 0
              ? 'Select at least one participant to pair'
              : undefined
          }>
          <ExperienceButton
            variant='primary'
            disabled={selectedCount === 0}
            onClick={openPairing}>
            Wisdom Guide Pairing
          </ExperienceButton>
        </span>
      </div>

      <div
        ref={tableWrapRef}
        className='experience-tbl-wrap experience-gp-tbl-wrap'>
        <table className='experience-tbl'>
          <thead>
            <tr>
              <th className='experience-gp-sel'>
                <input
                  className='experience-gp-check'
                  type='checkbox'
                  aria-label='Select all'
                  checked={allVisibleChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleChecked
                  }}
                  onChange={toggleAllVisible}
                  disabled={visibleRows.length === 0}
                />
              </th>
              <th>Participant</th>
              <th>Latest Assessment</th>
              <th>Wisdom Guide</th>
              <th>SOWs</th>
              <th>Scheduled Sessions</th>
              {answerQuestion ? (
                <th>{getQuestionTitle(answerQuestion)}</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 || isLoading
              ? renderEmptyRow()
              : participants.map((p, idx) => {
                  const id = getParticipantId(p)
                  const name = getParticipantName(p)
                  const email = getParticipantEmail(p)
                  const assessment = getParticipantLatestAssessmentParts(p)
                  const assessmentLink = getParticipantLatestAssessmentLink(p)
                  const sows = getParticipantSOWs(p)
                  const guides = getParticipantGuides(p).filter(
                    (g) => typeof g === 'object',
                  )
                  const guideNames = guides
                    .map((g) => getGuideLabel(g))
                    .filter(Boolean)
                  return (
                    <tr key={id || `participant-${idx}`}>
                      <td className='experience-gp-sel'>
                        <input
                          className='experience-gp-check'
                          type='checkbox'
                          checked={!!selected[id]}
                          disabled={!id}
                          onChange={() => toggleOne(p)}
                          aria-label={`Select ${name}`}
                        />
                      </td>
                      <td className='experience-who'>
                        <b>
                          {id ? (
                            <Link
                              to={`/admin/users/${id}/overview`}
                              rel='noopener noreferrer'
                              target='_blank'
                              className='text-dark-900'>
                              {name}
                            </Link>
                          ) : (
                            name
                          )}
                        </b>
                        {email ? <span>{email}</span> : null}
                        <span title='Signup date'>
                          {getParticipantSignupDate(p)}
                        </span>
                      </td>
                      <td>
                        {assessment && assessmentLink ? (
                          <a
                            className='experience-gp-assessment'
                            href={assessmentLink}
                            target='_blank'
                            rel='noopener noreferrer'>
                            {renderAssessment(assessment)}
                          </a>
                        ) : assessment ? (
                          <span className='experience-gp-assessment'>
                            {renderAssessment(assessment)}
                          </span>
                        ) : (
                          'None Taken'
                        )}
                      </td>
                      <td>
                        {guideNames.length
                          ? guideNames.map((g, i) => <div key={i}>{g}</div>)
                          : '—'}
                      </td>
                      <td>
                        {sows.length
                          ? sows.map((sow, i) => (
                              <div key={i}>
                                <TruncatedText
                                  text={sow}
                                  max={SOW_NAME_MAX_LENGTH}
                                  cursor='default'
                                />
                              </div>
                            ))
                          : '—'}
                      </td>
                      <td>
                        {guides.length
                          ? guides.map((g, i) => (
                              <div key={i} className='mb-2'>
                                {renderSessions(
                                  g.upcomingSessions,
                                  `${id}-${i}`,
                                )}
                              </div>
                            ))
                          : '—'}
                      </td>
                      {answerQuestion ? (
                        <td>{getParticipantAnswer(p, answerQuestion)}</td>
                      ) : null}
                    </tr>
                  )
                })}
            {isLoadingMore ? (
              <tr>
                <td colSpan={colCount} className='text-center py-2'>
                  <FeatherLoader />
                </td>
              </tr>
            ) : null}
          </tbody>
          {hasMore && !isLoading ? (
            <tfoot>
              <tr>
                <td colSpan={colCount} style={{ border: 0, padding: 0 }}>
                  <div ref={loaderRef} style={{ height: 1 }} />
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      <QuestGuidePairingModal
        show={showPairModal}
        participants={selectedParticipants}
        guides={guides}
        isLoadingGuides={isLoadingGuides}
        pairingState={pairingState}
        setPairingState={setPairingState}
        onCancel={resetFlow}
        onNext={handlePairingNext}
      />

      <QuestAssignSOWModal
        show={showSowModal}
        pairs={pendingPairs}
        guides={guides}
        schedulingMeta={schedulingMeta}
        currentQuest={
          questId ? { value: questId, label: questDetail?.name || '' } : null
        }
        initialValues={
          sowDraft ||
          getInitialSOWValues({
            questId,
            canUsePlatformScheduling: !schedulingMeta?.forceManualScheduling,
          })
        }
        onBackToPairing={handleBackToPairing}
        onCancel={resetFlow}
        onAssigned={handleAssigned}
      />
    </div>
  )
}

QuestGuidePairing.propTypes = {
  questDetail: PropTypes.object,
  enrollmentQuestions: PropTypes.arrayOf(PropTypes.object),
  onCompletenessChange: PropTypes.func,
}

QuestGuidePairing.defaultProps = {
  questDetail: null,
  enrollmentQuestions: [],
  onCompletenessChange: undefined,
}

export default QuestGuidePairing
